import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { SERVICES } from "@/lib/api";
import { SERVICE_MEDIA } from "@/lib/media";
import EventsSection from "@/components/EventsSection";
import { Star, ArrowRight, X as CloseIcon } from "@phosphor-icons/react";

export default function ServiceDetail() {
  const { serviceKey } = useParams();
  const service = SERVICES.find((s) => s.key === serviceKey);
  const media = SERVICE_MEDIA[serviceKey];
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, avg: 0 });
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!service) return;
    api.get(`/reviews?service=${serviceKey}`).then((r) => setReviews(r.data));
    api.get(`/reviews/stats?service=${serviceKey}`).then((r) => {
      setStats(r.data[serviceKey] || { count: 0, avg: 0 });
    });
  }, [serviceKey, service]);

  if (!service) {
    return (
      <div className="p-20 text-center">
        Service not found. <Link to="/services" className="underline">Browse services</Link>
      </div>
    );
  }

  return (
    <div data-testid={`service-detail-${serviceKey}`}>
      {/* Hero */}
      <section className="relative h-[70vh] overflow-hidden border-b border-black/10 text-white">
        <img src={service.image} alt={service.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-16">
          <p className="label-eyebrow text-white/80">{serviceKey.toUpperCase()}</p>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] mt-3 max-w-3xl">
            {service.name}
          </h1>
          <p className="mt-4 text-white/80 max-w-xl">{service.description}</p>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <Link to={`/book/${serviceKey}`} className="btn-sharp bg-white text-black border-white hover:bg-transparent hover:text-white" data-testid="detail-book-now">
              Book now <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="btn-sharp bg-transparent text-white border-white hover:bg-white hover:text-black">All services</Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-black p-5">
            <p className="label-eyebrow text-black/50">Rating</p>
            <p className="font-display text-3xl font-black mt-2 flex items-center gap-2">
              {stats.avg ? stats.avg.toFixed(1) : "—"}
              {stats.avg > 0 && <Star size={20} weight="fill" className="text-[#FF3333]" />}
            </p>
          </div>
          <div className="border border-black p-5">
            <p className="label-eyebrow text-black/50">Reviews</p>
            <p className="font-display text-3xl font-black mt-2">{stats.count}</p>
          </div>
          <div className="border border-black p-5">
            <p className="label-eyebrow text-black/50">Hours</p>
            <p className="font-display text-xl font-black mt-2">24 / 7</p>
          </div>
          <div className="border border-black p-5">
            <p className="label-eyebrow text-black/50">Location</p>
            <p className="font-display text-xl font-black mt-2">Accra</p>
          </div>
        </div>
      </section>

      {/* MEDIA GALLERY — only when SERVICE_MEDIA has an entry */}
      {media && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-8" data-testid="service-media">
          <div className="border-t border-black/10 pt-16">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
              <div>
                <p className="label-eyebrow">Live from the arena</p>
                <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-none mt-3">
                  {media.headline}
                </h2>
                {media.subhead && <p className="text-black/60 mt-3 text-sm max-w-xl">{media.subhead}</p>}
              </div>
              <p className="font-mono text-xs text-black/40">
                {media.photos.length} PHOTOS{(media.videos?.length || 0) > 0 ? ` · ${media.videos.length} VIDEO${media.videos.length > 1 ? "S" : ""}` : ""}
              </p>
            </div>

            {/* Photo grid */}
            <div className="grid grid-cols-2 md:grid-cols-12 gap-3" data-testid="boxing-gallery">
              {media.photos.map((p, i) => {
                const span = i === 0 ? "md:col-span-8 md:row-span-2 aspect-[16/10] md:aspect-auto md:h-[520px]" :
                             i === 1 ? "md:col-span-4 h-[256px]" :
                             i === 2 ? "md:col-span-4 h-[256px]" :
                             i === 3 ? "md:col-span-6 h-[320px]" :
                             "md:col-span-6 h-[320px]";
                return (
                  <button
                    type="button"
                    key={p.url}
                    onClick={() => setLightbox(i)}
                    className={`group relative overflow-hidden border border-black bg-black ${span}`}
                    data-testid={`gallery-photo-${i}`}
                  >
                    <img src={p.url} alt={p.caption} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <p className="absolute bottom-3 left-4 right-4 text-left text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      {p.caption}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Videos */}
            {media.videos?.length > 0 && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="service-videos">
                {media.videos.map((v, i) => (
                  <figure key={v.url} className="border border-black bg-black" data-testid={`service-video-${i}`}>
                    <video
                      controls
                      preload="metadata"
                      poster={media.photos[0]?.url}
                      className="w-full aspect-video object-contain bg-black"
                    >
                      <source src={v.url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    {v.caption && (
                      <figcaption className="bg-black text-white/80 text-xs font-mono px-3 py-2">{v.caption}</figcaption>
                    )}
                  </figure>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {media && lightbox !== null && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <CloseIcon size={32} />
          </button>
          <button
            className="absolute left-4 text-white/60 hover:text-white text-3xl font-light px-3"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + media.photos.length) % media.photos.length); }}
            aria-label="Previous"
          >‹</button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={media.photos[lightbox].url} alt={media.photos[lightbox].caption} className="w-full max-h-[80vh] object-contain" />
            <p className="text-white/80 text-sm mt-4 text-center">{media.photos[lightbox].caption}</p>
            <p className="text-white/40 font-mono text-xs mt-2 text-center">{lightbox + 1} / {media.photos.length}</p>
          </div>
          <button
            className="absolute right-4 text-white/60 hover:text-white text-3xl font-light px-3"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % media.photos.length); }}
            aria-label="Next"
          >›</button>
        </div>
      )}

      {/* Reviews */}
      <EventsSection serviceKey={serviceKey} serviceName={service.name} />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-eyebrow">Customer reviews</p>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-none mt-3">
              What guests are saying.
            </h2>
          </div>
          <Link to="/dashboard" className="btn-outline hidden md:inline-flex" data-testid="detail-leave-review">Leave a review</Link>
        </div>
        {reviews.length === 0 ? (
          <div className="border border-dashed border-black/30 p-12 text-center">
            <p className="text-black/60">No reviews yet — be the first after your visit.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {reviews.map((r) => (
              <article key={r.id} className="border border-black p-6 flex flex-col" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-1 text-[#FF3333]">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={16} weight="fill" />)}
                  {Array.from({ length: 5 - r.rating }).map((_, i) => <Star key={`e${i}`} size={16} className="text-black/20" />)}
                </div>
                <p className="mt-3 text-sm leading-relaxed flex-1">{r.comment || "—"}</p>
                <div className="mt-4 pt-4 border-t border-black/10 flex items-center justify-between">
                  <p className="font-semibold text-sm">{r.user_name}</p>
                  <p className="font-mono text-xs text-black/50">{r.created_at?.slice(0,10)}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
